import React, { useState } from 'react';

import Image from 'next/image';
import { useRouter } from 'next/router';
import {
    FacebookMessengerShareButton,
    FacebookShareButton,
    TwitterShareButton,
    WhatsappShareButton,
} from 'react-share';

import Button from '@components/common/Button/Button';
import useTranslateLpc from '@components/common/hooks/useTranslateLpc';
import Modal from '@components/common/Modals/Modal/Modal';
import ShareEmail from '@components/lpc/Pot/MainModule/Informations/ShareEmail/ShareEmail';
import { personRoles } from '@models/PotModel/personRoles';
import { getFullRoute, getRoute, ROUTE } from '@services/http/Route';
import StringService from '@services/utils/StringService';

import css from './ShareModal.module.scss';

type ShareModalProps = {
    isVisible: boolean;
    onHide: () => void;
    customClass?: string;
    potRef?: string;
    personRole?: string;
    isOffered?: boolean;
    potLink?: string;
    namePot?: string;
};

const ShareModal = ({ isVisible, onHide, customClass, potRef, personRole, isOffered, namePot }: ShareModalProps) => {
    const { t } = useTranslateLpc(['lpc-pot', 'common']);
    const [isCopied, setIsCopied] = useState<boolean>(false);
    const [openMail, setOpenMail] = useState<boolean>(false);
    const router = useRouter();
    const mailtoLink = namePot
        ? `mailto:?subject=${encodeURIComponent(t('informations.mail-subject', { namePot: namePot }))}&body=${encodeURIComponent(t('informations.mail-body', { url: getFullRoute(getRoute(ROUTE.LPC.POT.POT, potRef)), namePot: namePot }))}`
        : `mailto:?subject=${encodeURIComponent(t('competition-game.subject'))}&body=${encodeURIComponent(t('competition-game.body', { competitionLink: getFullRoute(router.asPath) }))}`;

    return (
        <>
            {personRole === personRoles.ORGANISER && !isOffered && (
                <ShareEmail
                    potRef={potRef}
                    isVisible={openMail}
                    onHide={() => setOpenMail(false)}
                    onConfirm={() => setOpenMail(false)}
                />
            )}

            <Modal
                visible={isVisible}
                onHide={() => {
                    onHide();
                    setIsCopied(false);
                }}
                customClass={css.custom_share_modal}
            >
                <div className={css.custom_share_modal__header}>
                    <h5>{t('competition-game.share')}</h5>
                </div>
                <div
                    className={`${css.custom_share_modal__social} ${isVisible && css.custom_share_modal__social__open} ${customClass || ''}`}
                >
                    <span className={css.custom_share_modal__social__cta}>
                        <WhatsappShareButton url={getFullRoute(router.asPath)}>
                            <Image
                                height={24}
                                width={24}
                                className={css.custom_share_modal__social__cta__img}
                                src="/front-static/icons/social/whats-app.svg"
                                alt="whatsapp"
                            />
                            {t('informations.whatsapp')}
                        </WhatsappShareButton>
                    </span>
                    <span className={css.custom_share_modal__social__cta}>
                        <FacebookMessengerShareButton
                            appId={process.env.FACEBOOK_APP_ID}
                            url={getFullRoute(router.asPath)}
                        >
                            <Image
                                height={24}
                                width={24}
                                className={css.custom_share_modal__social__cta__img}
                                src="/front-static/icons/social/messenger.svg"
                                alt="messenger"
                            />
                            {t('informations.messenger')}
                        </FacebookMessengerShareButton>
                    </span>
                    <span className={css.custom_share_modal__social__cta}>
                        <FacebookShareButton url={getFullRoute(router.asPath)}>
                            <Image
                                height={24}
                                width={24}
                                className={css.custom_share_modal__social__cta__img}
                                src="/front-static/icons/social/facebook.svg"
                                alt="facebook"
                            />
                            {t('informations.facebook')}
                        </FacebookShareButton>
                    </span>
                    <span className={css.custom_share_modal__social__cta}>
                        <TwitterShareButton url={getFullRoute(router.asPath)}>
                            <Image
                                height={24}
                                width={24}
                                className={css.custom_share_modal__social__cta__img}
                                src="/front-static/icons/social/twitter.svg"
                                alt="twitter"
                            />
                            {t('informations.twitter')}
                        </TwitterShareButton>
                    </span>
                    {personRole === personRoles.ORGANISER && !isOffered && potRef ? (
                        <span className={css.custom_share_modal__social__cta} onClick={() => setOpenMail(true)}>
                            <Image
                                height={24}
                                width={24}
                                className={css.custom_share_modal__social__cta__img}
                                src="/front-static/icons/social/mail.svg"
                                alt="mail"
                            />
                            {t('informations.mail')}
                        </span>
                    ) : (
                        <span
                            className={css.custom_share_modal__social__cta}
                            onClick={() => (window.location.href = mailtoLink)}
                        >
                            <Image
                                height={24}
                                width={24}
                                className={css.custom_share_modal__social__cta__img}
                                src="/front-static/icons/social/mail.svg"
                                alt="mail"
                            />
                            {t('informations.mail')}
                        </span>
                    )}
                    <section className={css.custom_share_modal__form_share_modal_competitions}>
                        <div className={css.custom_share_modal__form_share_modal_competitions__header}>
                            <h5>{t('competition-game.copy-competition-link')}</h5>
                        </div>
                        <div className={css.custom_share_modal__form_share_modal_competitions__copy}>
                            <input
                                className={css.custom_share_modal__form_share_modal_competitions__copy__input}
                                type="text"
                                placeholder={getFullRoute(router.asPath)}
                                onClick={() => {
                                    StringService.copyTextToClipboard(
                                        StringService.getUrlWithoutParams(window.location.href),
                                    );
                                    setIsCopied(true);
                                }}
                            />
                            <Button
                                onClick={() => {
                                    StringService.copyTextToClipboard(
                                        StringService.getUrlWithoutParams(window.location.href),
                                    );
                                    setIsCopied(true);
                                }}
                                variant={'white'}
                                customClass={
                                    isCopied
                                        ? `${css.custom_share_modal__form_share_modal_competitions__copy__cta__is_copied} ${css.custom_share_modal__form_share_modal_competitions__copy__cta}`
                                        : `${css.custom_share_modal__form_share_modal_competitions__copy__cta}`
                                }
                                customContentClass={
                                    css.custom_share_modal__form_share_modal_competitions__copy__cta__copied
                                }
                            >
                                {!isCopied ? (
                                    t('informations.cta-copy')
                                ) : (
                                    <Image
                                        height={24}
                                        width={24}
                                        src="/front-static/icons/display/checkmark-outline-primary.svg"
                                        alt="copied"
                                    />
                                )}
                            </Button>
                        </div>
                    </section>
                </div>
            </Modal>
        </>
    );
};

export default ShareModal;
